import clsx from 'clsx';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';

import { useDocumentId } from '@/hooks/use-document-id';
import { useItemActions } from '@/hooks/use-item-actions';
import { useResolvedUserSourceIds, useNote, useUserSourceCreate, useUserSourceMakeGlobal } from '@/hooks/rxdb-models';
import { type UploadingItem, useUserSourceUpload } from '@/hooks/use-userSource-upload';

import { openSourcePreview } from '@/utils/source-preview';
import { isFileNotFoundError } from '@/services/firebase-storage-service';
import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';

import { Tab } from '@flintmint/web-shared/components/tab';
import { Tabs } from '@flintmint/web-shared/components/tabs';
import { AddContextItems } from '@/components/add-context-items';
import { AudioPreviewModal } from '@/components/audio-preview-modal';
import { TabPanel } from '@flintmint/web-shared/components/tab-panel';
import { CortexPreviewModal } from '@/components/cortex-preview-modal';
import { TabPanels } from '@flintmint/web-shared/components/tab-panels';
import { AiContextModalFrame } from '@/components/ai-context-modal-frame';
import { ManagedSourcesGrid } from '@/components/context-items-modal/managed-sources-grid';

import { type ContextItemProps } from '@/components/context-item';
import type { UserSource, UserSourceAvailabilityScope } from '@flintmint/core-shared/rxdb';

import styles from './context-items-modal.module.scss';

export type UploadFlowStep = 'add-items' | 'manage-items';

// An uploading row keeps its local id until the created source is observed attached to the note;
// createdId links the two so the row can be retired once the reactive source takes over.
type ModalUploadItem = UploadingItem & { createdId?: string };

export const ContextItemsModal = (props: ContextItemsModalProps) => {
  const {
    open,
    onClose,
    initialStep,
    noteVariant,
    onAttachToNote,
    onDetachFromNote,
    onReplaceWithNew,
    onUserSourceEnabledChange,
  } = props;

  const [mode, setMode] = useState<UploadFlowStep>('add-items');
  const [localItems, setLocalItems] = useState<ModalUploadItem[]>([]);
  const [uploadCompletionCount, setUploadCompletionCount] = useState(0);
  const [pendingDetachedIds, setPendingDetachedIds] = useState<string[]>([]);
  const [cortexPreview, setCortexPreview] = useState<{ id: string }>();
  const [audioPreviewId, setAudioPreviewId] = useState<string>();
  const [activeTab, setActiveTab] = useState('on-this-note');

  const localItemsRef = useRef<ModalUploadItem[]>([]);

  const { mutate: createUserSource } = useUserSourceCreate();
  const { mutate: makeUserSourceGlobal } = useUserSourceMakeGlobal();

  const { id: noteId, isDraft } = useDocumentId();

  const { data: note } = useNote(noteId);
  const noteUserSources = useMemo(() => note?.toJSON().userSources || {}, [note]);
  const rawAttachedUserSourceIds = useMemo(() => Object.keys(noteUserSources), [noteUserSources]);

  const attachedUserSourceIds = useResolvedUserSourceIds(rawAttachedUserSourceIds);

  const { configureDestructiveOptions, createNew } = useItemActions();
  const { showToastNotification } = useToaster();

  const handleCreateBlankItem = () => {
    createNew({
      type: 'userSources',
      originNoteLink: noteId ? { id: noteId, variant: noteVariant, isDraft } : undefined,
    });
  };

  const visibleAttachedIds = useMemo(
    () => attachedUserSourceIds.filter((id) => !pendingDetachedIds.includes(id)),
    [attachedUserSourceIds, pendingDetachedIds]
  );

  // Hide it here for the render, and prune it from state in the effect below.
  const visibleLocalItems = useMemo(
    () => localItems.filter((item) => !isRetiredUpload(item, attachedUserSourceIds)),
    [localItems, attachedUserSourceIds]
  );

  useEffect(() => {
    localItemsRef.current = localItems;
  }, [localItems]);

  useEffect(() => {
    setLocalItems((prev) => {
      const next = prev.filter((item) => !isRetiredUpload(item, attachedUserSourceIds));
      return next.length === prev.length ? prev : next;
    });
  }, [attachedUserSourceIds]);

  const updateUploadingItem = useCallback((id: string, updater: (item: UploadingItem) => UploadingItem) => {
    setLocalItems((prev) => prev.map((item) => (item.id === id ? updater(item) : item)));
  }, []);

  const { startUpload, prepareFiles } = useUserSourceUpload({
    availabilityScope: 'note',
    onProgress: updateUploadingItem,
    onError: updateUploadingItem,
    onComplete: (created, localId) => {
      if (!localItemsRef.current.some((item) => item.id === localId)) return;
      setLocalItems((prev) => prev.map((item) => (item.id === localId ? { ...item, createdId: created.id } : item)));
      setUploadCompletionCount((count) => count + 1);
      Promise.resolve(onAttachToNote?.([created.id])).catch(() => {
        setLocalItems((prev) => prev.filter((item) => item.id !== localId));
      });
    },
  });

  useEffect(() => {
    if (!open) return;

    const nextLocalItems = localItemsRef.current.filter((item) => item.status !== 'error');
    if (nextLocalItems.length !== localItemsRef.current.length) {
      localItemsRef.current = nextLocalItems;
      setLocalItems(nextLocalItems);
    }

    const hasItems = nextLocalItems.length > 0 || attachedUserSourceIds.length > 0;
    setMode(initialStep ?? (hasItems ? 'manage-items' : 'add-items'));
    setPendingDetachedIds([]);
    // deps: attachedUserSourceIds intentionally excluded
  }, [open, initialStep]);

  useEffect(() => {
    if (
      open &&
      initialStep !== 'manage-items' &&
      !attachedUserSourceIds.length &&
      !localItems.length &&
      mode === 'manage-items'
    ) {
      setMode('add-items');
    }
    // deps: mode is intentionally excluded from the dependency array
  }, [attachedUserSourceIds.length, localItems.length, open, initialStep]);

  useEffect(() => {
    setPendingDetachedIds((prev) => {
      const next = prev.filter((id) => attachedUserSourceIds.includes(id));
      return next.length === prev.length ? prev : next;
    });
  }, [attachedUserSourceIds]);

  const handleClose = () => {
    onClose?.();
    setActiveTab('on-this-note');
  };

  const handleFiles = (files: FileList | File[] | null) => {
    const items = prepareFiles(files);
    if (!items.length) return;

    setLocalItems((prev) => [...prev, ...items]);
    setMode('manage-items');
    setActiveTab('on-this-note');
    items.filter((i) => i.status === 'uploading').forEach(startUpload);
  };

  const handleUrlAdd = (url: string) => {
    createUserSource(
      { name: url, sourceType: 'url', source: url, syncStatus: 'processing', availabilityScope: 'note' },
      {
        onSuccess: (created: UserSource) => {
          // No local row for URL adds; the attached row appears once the attach propagates into
          // the note. On failure the source stays in the library, so consciously swallow.
          Promise.resolve(onAttachToNote?.([created.id])).catch(() => {});
          setMode('manage-items');
          setActiveTab('on-this-note');
        },
      }
    );
  };

  const openAddContextItems = () => {
    setMode('add-items');
  };

  const toggleAttachment = (sourceId: string, attached: boolean) => {
    if (attached) {
      handleDetachFromNote(sourceId);
      return;
    }

    setPendingDetachedIds((prev) => prev.filter((pendingId) => pendingId !== sourceId));
    onAttachToNote?.([sourceId]);
  };

  const handleDetachFromNote = (id: string) => {
    setPendingDetachedIds((prev) => appendUnique(prev, id));
    setLocalItems((prev) => prev.filter((item) => item.id !== id));
    Promise.resolve(onDetachFromNote?.([id])).catch(() => {
      setPendingDetachedIds((prev) => prev.filter((pendingId) => pendingId !== id));
    });
  };

  const getSharedContextItemProps = (
    id: string,
    name: string,
    sourceType?: string | null,
    source?: string | null,
    uploading = false,
    availabilityScope?: UserSourceAvailabilityScope
  ): Partial<ContextItemProps> => {
    return !uploading
      ? {
          onPreview: () =>
            void openSourcePreview({
              id,
              sourceType,
              source,
              onOpenPreview: (previewId) => {
                setCortexPreview({
                  id: previewId,
                });
              },
            }).catch((error) => {
              if (!isFileNotFoundError(error)) throw error;
              showToastNotification({
                type: 'error',
                title: 'File unavailable',
                description: 'This file is no longer available.',
              });
            }),
          onDetachFromNote: () => handleDetachFromNote(id),
          deleteOption: configureDestructiveOptions([{ id, name, type: 'userSources', availabilityScope }], {
            onDone: () => {
              setLocalItems((prev) => prev.filter((item) => item.id !== id));
            },
          }),
          onMakeGloballyAvailable:
            availabilityScope === 'note'
              ? () => {
                  void makeUserSourceGlobal(id);
                }
              : undefined,
        }
      : {};
  };

  const modalTitle = mode === 'add-items' ? 'Add Sources' : 'Sources';
  const isCortexPreviewAttached =
    !!cortexPreview?.id && !!noteUserSources[cortexPreview.id] && !pendingDetachedIds.includes(cortexPreview.id);

  return (
    <>
      <AiContextModalFrame
        open={open}
        onClose={handleClose}
        title={modalTitle}
        showFooter={false}
        className={clsx(styles.modalWrapper, {
          [styles.manageItems]: mode === 'manage-items',
        })}
      >
        {mode === 'add-items' && (
          <AddContextItems onFiles={handleFiles} onUrlAdd={handleUrlAdd} onCreateBlankItem={handleCreateBlankItem} />
        )}
        {mode === 'manage-items' && (
          <>
            <div className={styles.managedItems}>
              <Tabs value={activeTab} appearance="elevated" size="small" onChange={setActiveTab}>
                <Tab id="on-this-note" name="Attached to this Note" />
                <Tab id="from-source-library" name="From Source Library" />
              </Tabs>
              <TabPanels value={activeTab}>
                <TabPanel id="on-this-note">
                  <ManagedSourcesGrid
                    tabId="on-this-note"
                    ids={visibleAttachedIds}
                    resetSearchKey={uploadCompletionCount}
                    noteUserSources={noteUserSources}
                    onDetach={handleDetachFromNote}
                    toggleAttachment={toggleAttachment}
                    onUserSourceEnabledChange={onUserSourceEnabledChange}
                    onOpenAudioPreview={setAudioPreviewId}
                    getSharedContextItemProps={getSharedContextItemProps}
                    openAddContextItems={openAddContextItems}
                    localItems={visibleLocalItems}
                    onRemoveLocalItem={(id) => setLocalItems((prev) => prev.filter((item) => item.id !== id))}
                    renderLocalItems
                    showNoAttachedState={!visibleAttachedIds.length && !visibleLocalItems.length}
                    noAttachedFilesView={
                      <AddContextItems onFiles={handleFiles} onUrlAdd={handleUrlAdd} onCreateBlankItem={handleCreateBlankItem} />
                    }
                  />
                </TabPanel>
                <TabPanel id="from-source-library">
                  <ManagedSourcesGrid
                    showAllUserSources
                    tabId="from-source-library"
                    onDetach={handleDetachFromNote}
                    noteUserSources={noteUserSources}
                    toggleAttachment={toggleAttachment}
                    onOpenAudioPreview={setAudioPreviewId}
                    openAddContextItems={openAddContextItems}
                    onUserSourceEnabledChange={onUserSourceEnabledChange}
                    getSharedContextItemProps={getSharedContextItemProps}
                  />
                </TabPanel>
              </TabPanels>
            </div>
          </>
        )}
      </AiContextModalFrame>
      {!!cortexPreview?.id && (
        <CortexPreviewModal
          open
          userSourceId={cortexPreview.id}
          noteVariant={noteVariant}
          userSourceAttached={isCortexPreviewAttached}
          onReplaceWithNew={onReplaceWithNew}
          toggleAttachment={toggleAttachment}
          onClose={() => setCortexPreview(undefined)}
        />
      )}
      <AudioPreviewModal open={!!audioPreviewId} sourceId={audioPreviewId} onClose={() => setAudioPreviewId(undefined)} />
    </>
  );
};

const isRetiredUpload = (item: ModalUploadItem, attachedIds: string[]) =>
  !!item.createdId && attachedIds.includes(item.createdId);

const appendUnique = (ids: string[], id: string) => (ids.includes(id) ? ids : [...ids, id]);

export interface ContextItemsModalProps {
  open: boolean;
  onClose?: () => void;
  initialStep?: UploadFlowStep;
  noteVariant?: string;
  onAttachToNote?: (userSourceIds: string[]) => void | Promise<void>;
  onDetachFromNote?: (userSourceIds: string[]) => void | Promise<void>;
  onUserSourceEnabledChange?: (userSourceId: string, enabled: boolean) => void | Promise<void>;
  onReplaceWithNew: (attachedId: string, newId: string) => void | Promise<void>;
}
